<template>
  <template v-if="showData(cartStore)">
    <CheckoutCard>
      <div class="flex flex-col gap-3">
        <div class="flex justify-between items-center">
          <h3 class="font-medium text-lg">{{ $t("Guest information") }}</h3>
          <div
            class="flex items-center gap-1 text-muted-foreground cursor-pointer select-none"
            @click="showMoreInformation = !showMoreInformation">
            <div class="text-xs">{{ $t(!showMoreInformation ? "More information" : "Less information") }}</div>
            <InformationCircleIcon class="size-4" />
          </div>
        </div>
        <div v-if="showMoreInformation" class="text-sm bg-info-muted text-muted-foreground rounded-sm p-4 flex gap-2">
          <InformationCircleIcon class="size-4 flex-none" />
          {{
            isB2C
              ? $t(
                  "For some events, we need to deliver personalized tickets. Therefore, we require a few additional personal details related to your booking."
                )
              : $t(
                  "The venue requires personalized tickets for this event. We therefore need a few additional personal details to complete your booking."
                )
          }}
        </div>
        <Checkbox
          v-if="conditions.length && !conditions.includes('pre_checkout') && !isB2C"
          name="addGuestsLater"
          :label="$t(`I'll add guest info later`)" />
      </div>

      <template v-for="item of cartStore.conditions.filter((item) => item.guests.length)" :key="item.ticket_id">
        <div v-if="!addGuestsLaterField.value.value" class="flex flex-col gap-4 rounded-lg">
          <GuestDataTicketInfo
            v-for="info of [cartStore.getItem(item.ticket_id)].filter(Boolean)"
            :key="info.ticket.ticket_id"
            :event="info.event"
            :ticket="info.ticket"
            class="p-4 bg-muted text-muted-foreground" />
          <div class="rounded-lg border border-border">
            <div v-for="(guestdata, guest) of item.guests" :key="`${item.ticket_id}-${guest}`">
              <hr v-if="guest" class="border-border" />
              <GuestDataDetails
                v-model:required-fields="requiredFieldsByGuest[`${item.ticket_id}[${guest}]`]"
                :ticket-id="item.ticket_id"
                :guest="guest"
                :guestdata="guestdata"
                :event="cartStore.getItem(item.ticket_id)?.event"
                :hometeam="cartStore.getItem(item.ticket_id)?.event?.hometeam_id"
                :visiting="cartStore.getItem(item.ticket_id)?.event?.visiting_id"
                form-key-prefix="guests"
                class="px-4"
                @update-requirements="handleUpdateRequirements" />
            </div>
          </div>
        </div>
        <div v-if="message">
          <div class="fixed inset-0 flex justify-center items-center bg-black/10" @click="message = ''">
            <div class="bg-background p-8 flex flex-col items-center gap-4 rounded-xl shadow-xl w-1/5">
              <div v-if="message === 'success'">
                {{ $t("Guest information submitted.") }}
              </div>
              <div v-if="message === 'failure'">
                {{ $t("Invalid / incomplate guest information") }}
              </div>
              <div v-if="message === 'saving'">
                {{ $t("Submitting guest information, please wait...") }}
              </div>
              <div
                v-if="
                  message === 'sameEmailAsClient' &&
                  [cartStore.getItem(item.ticket_id)].some((i) =>
                    [LIVERPOOL_HASHKEY, MANCHESTER_UNITED_HASHKEY].includes(i.event.hometeam_id)
                  )
                ">
                {{
                  $t(
                    "Unless you will personally be joining your client at the stadium to allow him access through the turn stiles we strongly advise to fill in your customer's email address.Your client will receive their mobile tickets directly from the club, either via email or through the club's mobile app. He will need to open the tickets himself at the stadium turnstiles."
                  )
                }}
              </div>
              <button class="bg-primary px-4 py-2 rounded" @click="message = ''">
                {{ $t("Ok") }}
              </button>
            </div>
          </div>
        </div>
      </template>
      <div v-if="cartStore.addGuestDataLater" class="flex flex-col lg:gap-4 p-4 lg:p-0 bg-box rounded-lg">
        <p class="px-4 py-4">
          {{
            $t(
              "After completing this booking, complete guest details as soon as possible by going to your Bookings in our portal"
            )
          }}.
        </p>
      </div>
    </CheckoutCard>
  </template>
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { LIVERPOOL_HASHKEY, MANCHESTER_UNITED_HASHKEY } from "@xs2event/js-lib";
import { Checkbox, GuestDataDetails } from "@xs2event/js-lib/components";
import GuestDataTicketInfo from "./GuestDataTicketInfo.vue";
import { CheckoutCard } from "./";
import { isB2C } from "@/brandconfig";
import { useCartStore } from "@/store/cart.ts";
import { useField } from "@xs2event/js-lib/validation";
import { injectCheckoutContext } from "./checkoutContext";

const cartStore = useCartStore();
const message = ref<null | string>(null);

const showMoreInformation = ref(false);

const addGuestsLaterField = useField<boolean | undefined>("addGuestsLater");

cartStore.fetchGuestData();

const showData = (cartStore: ReturnType<typeof useCartStore>) =>
  cartStore.conditions.some((item) => item.guests.length);

const conditions = computed(() => {
  const result: unknown[] = [];

  cartStore.conditions.forEach((tickets) => {
    tickets.guests.forEach((guest) => {
      Object.values(guest).forEach((i) => {
        if ((i as { condition?: unknown }).condition) {
          result.push((i as { condition?: unknown }).condition as unknown);
        }
      });
    });
  });

  return result;
});

const { guestConditionOverwrites } = injectCheckoutContext();

const requiredFieldsByGuest = ref<Record<string, string[]>>({});

watch(
  () => cartStore.conditions,
  (conditions) => {
    const map: Record<string, string[]> = {};

    conditions.forEach((tickets: { ticket_id: string; guests: Record<string, unknown>[] }) => {
      tickets.guests.forEach((guestdata, guestIndex) => {
        const key = `${tickets.ticket_id}[${guestIndex}]`;
        map[key] = Object.keys(guestdata || {}).filter((field) => field !== "lead_guest");
      });
    });

    requiredFieldsByGuest.value = map;
  },
  { immediate: true, deep: true }
);

const handleUpdateRequirements = (payload: { ticketId: string; guest: number; fields: string[] }) => {
  const key = `${payload.ticketId}[${payload.guest}]`;

  guestConditionOverwrites.value.set(key, Object.fromEntries(payload.fields.map((field) => [field, field])));

  requiredFieldsByGuest.value[key] = payload.fields;
};
</script>
